import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect } from 'react'

import { getBinaryFileContentBySignedUrl } from 'services'
import { QueryKeys } from 'services/queryKeys'
import { getSignedUrlForApprovedResult } from 'services/results'
import type { GetSignedUrlForApprovedResultParams, GetSignedUrlForApprovedResultResponse } from 'services/results/types'
import { downloadBlob } from 'utils/download'
import { getFileNameFromPath, getMIMETypeFromFileName } from 'utils/string'

/**
 * Hook that automatically downloads a file from an approved result.
 *
 * This hook performs a two-step process:
 * 1. Fetches a signed URL for the approved result using the provided resultId and token
 * 2. Once the signed URL is available, fetches the file contents and automatically triggers a download
 *
 * The download uses the file key from the signed URL response to determine the filename
 * and MIME type, then creates a blob download in the browser.
 * This also makes sure that the file is always downloaded to the user's computer instead of being
 * previewed in the browser.
 *
 * @param resultId - The ID of the approved result to download
 * @param token - The token required to access the approved result
 * @returns Object with:
 *   - expiresAt: the expiry timestamp read from the failed `/access` response body (expired-link path only)
 *   - isLinkExpired: the `/access` call itself failed, i.e. the download link is invalid/expired
 *   - isDownloadError: `/access` succeeded but fetching the file blob failed (a download error, not link expiry)
 *   - isLoading: either request is in-flight
 */
export const useSignedUrlForApprovedResult = ({ resultId, token }: Partial<GetSignedUrlForApprovedResultParams>) => {
  const getSignedURLForApprovedResultQuery = useQuery({
    queryKey: QueryKeys.GetSignedUrlForApprovedResult({ resultId, token }),
    queryFn: () => getSignedUrlForApprovedResult({ resultId: resultId ?? '', token: token ?? '' }),
    retry: false
  })

  const { signedUrl, fileKey } = getSignedURLForApprovedResultQuery.data ?? {}
  const fileContentsQuery = useQuery({
    queryKey: QueryKeys.GetBinaryFileContentFromSignedUrl(signedUrl ?? ''),
    queryFn: ({ signal }) => getBinaryFileContentBySignedUrl(signedUrl!, { signal }),
    enabled: !!signedUrl,
    retry: false
  })

  useEffect(() => {
    // If the file has no content we should still download it to avoid user confusion.
    if (!fileContentsQuery.isSuccess || fileContentsQuery.data === undefined || !signedUrl || !fileKey) {
      return
    }

    const fileName = getFileNameFromPath(fileKey)
    if (!fileName) return

    const mimeType = getMIMETypeFromFileName(fileName)
    downloadBlob(fileContentsQuery.data, fileName, mimeType)
  }, [fileContentsQuery.isSuccess, fileContentsQuery.data, signedUrl, fileKey])

  // On a failed `/access` call the query never populates `data`, so the expiry
  // timestamp has to be read from the error response body instead. This is the
  // only place `expiresAt` should come from — on success there is nothing expired
  // to report.
  const accessError = getSignedURLForApprovedResultQuery.error
  const expiresAt = axios.isAxiosError(accessError)
    ? (accessError.response?.data as GetSignedUrlForApprovedResultResponse | undefined)?.expiresAt
    : undefined

  return {
    expiresAt,
    // The `/access` request failed → the download link itself is invalid/expired.
    isLinkExpired: getSignedURLForApprovedResultQuery.isError,
    // `/access` succeeded but the file blob could not be fetched → a download
    // failure, which must not be framed to the user as link expiry.
    isDownloadError: fileContentsQuery.isError,
    isLoading: getSignedURLForApprovedResultQuery.isLoading || fileContentsQuery.isLoading
  }
}
